/**
 * The preview panel's remembered state and the room to honour it, wired to the browser.
 *
 * The decision is `model/panelPreference`'s — which values are legal, what every unreadable answer
 * resolves to, at what width the column fits, and what `visible` therefore is. This is the clock
 * and the store: it reads the preference once on mount, writes it on every toggle, and subscribes
 * to the one media query.
 *
 * `useState`'s lazy initialiser rather than an effect, deliberately. An effect would render the
 * panel shown and then hide it a frame later on a trainer who had turned it off, which is the jump
 * the loading policy exists to prevent — and it is a jump of 396px plus the gap, on every entry.
 *
 * `useSyncExternalStore` for the width, for the same reason: its snapshot is read during the first
 * render rather than after it, so a window that is already narrow never draws the column at all.
 * `matchMedia` is absent in no browser this app supports, but it IS absent under a bare jsdom, so
 * the subscription degrades to "it fits" rather than throwing — which is the reading that keeps a
 * renderer-less test looking at the screen the canon draws.
 */

import { useState, useSyncExternalStore } from 'react';

import { preferenceStore } from '~/shared/lib/preferenceStore';

import {
  PANEL_FITS_QUERY,
  panelVisible,
  readPanelPreference,
  writePanelPreference,
  type PanelPreference,
} from '../model/panelPreference';

export interface PanelPreferenceState {
  /** What the trainer asked for, which is what is stored and what a wide window restores. */
  readonly panel: PanelPreference;
  /** Whether the window has the room. `false` below the breakpoint, whatever the preference is. */
  readonly fits: boolean;
  /** Whether the column is actually drawn. The one reading every part of the screen takes. */
  readonly visible: boolean;
  readonly toggle: () => void;
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => undefined;
  const query = window.matchMedia(PANEL_FITS_QUERY);
  query.addEventListener('change', onChange);
  return () => {
    query.removeEventListener('change', onChange);
  };
}

function readFits(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia(PANEL_FITS_QUERY).matches;
}

export function usePanelPreference(): PanelPreferenceState {
  const [panel, setPanel] = useState<PanelPreference>(() =>
    readPanelPreference(preferenceStore()),
  );
  const fits = useSyncExternalStore(subscribe, readFits, () => true);

  return {
    panel,
    fits,
    visible: panelVisible(panel, fits),
    toggle: () => {
      const next: PanelPreference = panel === 'shown' ? 'hidden' : 'shown';
      setPanel(next);
      writePanelPreference(preferenceStore(), next);
    },
  };
}
